import { createFileRoute, redirect, useRouter } from '@tanstack/react-router'
import { KeyRound } from 'lucide-react'
import { ChangePasswordForm } from '@/features/auth/change-password-form'
import { api, ensureSession } from '@/lib/api'
import { isTwoFactorChallenge, needsTwoFactorEnrollment, useAuthStore, refreshTokenStore, type LoginResponse } from '@/lib/auth'
import { TURNSTILE_SITE_KEY } from '@/lib/env'
import { notifySuccess } from '@/lib/notify'
import { ROUTES } from '@/lib/routes'
import { endSession, startSession } from '@/lib/session'

export const Route = createFileRoute('/_auth/change-password')({
  beforeLoad: async ({ location }) => {
    if (!(await ensureSession())) throw redirect({ to: ROUTES.login, search: { redirect: location.href } })
  },
  component: ForcedPasswordChange,
})

/**
 * Cambio obligatorio de contraseña (usuario nuevo o contraseña restablecida
 * por un administrador). El API cierra todas las sesiones al cambiarla; si
 * no hay captcha configurado, se vuelve a entrar automáticamente.
 */
function ForcedPasswordChange() {
  const router = useRouter()
  const employee = useAuthStore((s) => s.employee)

  const onChanged = async (values: { newPassword: string }) => {
    const email = employee?.email
    const remember = refreshTokenStore.isRemembered()
    useAuthStore.getState().clearSession()
    if (email && !TURNSTILE_SITE_KEY) {
      try {
        const response = await api.public.post<LoginResponse>('/auth/login', { email, password: values.newPassword })
        // Con verificación en dos pasos no hay sesión directa: se pide entrar con el código.
        if (!isTwoFactorChallenge(response)) {
          startSession(response, remember)
          notifySuccess('Contraseña actualizada', 'Bienvenido(a).')
          await router.navigate({ to: needsTwoFactorEnrollment(response.employee) ? ROUTES.twoFactorSetup : ROUTES.dashboard })
          return
        }
      } catch {
        /* se pide iniciar sesión manualmente */
      }
    }
    await endSession()
    notifySuccess('Contraseña actualizada', 'Inicie sesión con su nueva contraseña.')
    await router.navigate({ to: ROUTES.login })
  }

  return (
    <div className="rounded-2xl border border-border bg-card p-6 shadow-[0_8px_40px_hsl(var(--shadow-color)/0.08)] sm:p-8">
      <div className="mb-6 flex items-start gap-3">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <KeyRound className="size-5" aria-hidden />
        </span>
        <div>
          <h2 className="text-lg font-bold">Cree su contraseña</h2>
          <p className="text-sm text-muted-foreground">
            {employee?.fullName ? `${employee.fullName}, por` : 'Por'} seguridad debe cambiar la contraseña temporal antes de continuar.
          </p>
        </div>
      </div>
      <ChangePasswordForm onChanged={onChanged} submitLabel="Guardar y continuar" />
    </div>
  )
}
